import {
  type FocusEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Col,
  Row,
  Typography,
  Input,
  Button,
  Select,
  Form,
  InputNumber,
  Spin,
  message,
  DatePicker,
} from "antd";
import {
  QuestionCircleOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faBullseye,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { BusinessState } from "redux-context/business";
import { LocationAvailability } from "interfaces/location-availability";
import ModalVenue from "pages/collab/ModalVenue";
import Promotions from "pages/collab/Promotions";
import CollabSection from "pages/collab/CollabSection";
import {
  checkTierAvailability,
  getCollabsCampaignDrafts,
  postCollabsCampaign,
} from "services/SauceService";
import { scrollToTop } from "helpers/scroll-helpers";
import { formatNumberKMBT } from "pages/influencers/InfluencerCard";
import "./Campaign.scss";
import { debounce } from "lodash";
import dayjs from "dayjs";
import VenueSelectionModal from "components/venue-map/VenueSelectionModal";
import OfferStep from "./OfferStep";
import { getLoggedUserLocation, UserState } from "redux-context/user";
import { SubscriptionType } from "enums/SubscriptionType";
import { usePaywall } from "components/paywall/paywallContext";
import { PaywallSource } from "components/paywall/Paywall";
import IncentivizedReviewWarning from "components/incentivized-review-warning/IncentivizedReviewWarning";
import { detectIncentivizedReviewKeyword } from "helpers/incentivized-review-warning";
import {
  CampaignStepQuery,
  trackCampaignCollaborationContinueClicked,
  trackCampaignCreativeFreedomSelected,
  trackCampaignDetailsContinueClicked,
  trackCampaignInfluencerCountChanged,
  trackCampaignLocationAddOnMap,
  trackCampaignOfferContinueClicked,
  trackCampaignPlatformInstagramSelected,
  trackCampaignPlatformTiktokSelected,
  trackCampaignPromoOffersSelected,
  getCampaignStepMeta,
  trackCampaignCompensationGiftedSelected,
  trackCampaignCompensationPaidSelected,
  trackCampaignScheduleClicked,
  trackCampaignStartDateSelected,
  trackCampaignStepViewed,
} from "analytics/features/campaignCreation";

enum CampaignFormSteps {
  CampaignDetails = 0,
  Offer = 1,
  Promotion = 2,
  Instructions = 4,
}

const CAMPAIGN_STEP_QUERY_TO_FORM_STEP: Record<string, CampaignFormSteps> = {
  details: CampaignFormSteps.CampaignDetails,
  compensation: CampaignFormSteps.Offer,
  platform: CampaignFormSteps.Offer,
  offer: CampaignFormSteps.Offer,
  type: CampaignFormSteps.Promotion,
  promotion: CampaignFormSteps.Promotion,
  instructions: CampaignFormSteps.Instructions,
};

const CAMPAIGN_FORM_STEP_TO_QUERY: Record<CampaignFormSteps, string> = {
  [CampaignFormSteps.CampaignDetails]: "details",
  [CampaignFormSteps.Offer]: "compensation",
  [CampaignFormSteps.Promotion]: "type",
  [CampaignFormSteps.Instructions]: "instructions",
};

const ENABLE_CAMPAIGN_STEP_QUERY_ENTRY = false;

const getCampaignStepFromSearchParams = (
  params: URLSearchParams,
): CampaignFormSteps => {
  const queryStep = params.get("step")?.toLowerCase();
  if (!queryStep) return CampaignFormSteps.CampaignDetails;
  return (
    CAMPAIGN_STEP_QUERY_TO_FORM_STEP[queryStep] ??
    CampaignFormSteps.CampaignDetails
  );
};

const getCampaignStepQuery = (step: CampaignFormSteps): CampaignStepQuery => {
  return CAMPAIGN_FORM_STEP_TO_QUERY[step] as CampaignStepQuery;
};

const computeCreatorRecommendationRange = (
  locationCount: number,
): { low: number | null; high: number | null } => {
  if (locationCount < 2) return { low: null, high: null };
  const rec = locationCount * 3;
  const low = Math.max(1, Math.floor(rec * 0.8));
  const high = Math.min(999, Math.ceil(rec * 1.2));
  return { low, high: high < low ? low : high };
};

const CreatorsNumberInput: React.FC<{
  value?: number | null;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  locationCount?: number;
}> = ({ value, onChange, min = 1, max = 999, locationCount = 0 }) => {
  const current = value ?? min;

  const helperText = useMemo(() => {
    if (locationCount === 0) return null;
    if (locationCount === 1) {
      return "Most successful campaigns include 3-5 creators for balanced reach and engagement.";
    }
    const { low, high } = computeCreatorRecommendationRange(locationCount);
    if (low == null || high == null) return null;
    return `For ${locationCount} locations, we recommend ${low}–${high} creators for consistent coverage and stronger results.`;
  }, [locationCount]);

  return (
    <div className="campaign-creators-input-container">
      <div className="campaign-creators-input">
        <button
          type="button"
          className="campaign-creators-input-btn"
          onClick={() => onChange?.(Math.max(min, current - 1))}
        >
          <MinusOutlined />
        </button>
        <div className="campaign-creators-input-middle">
          <FontAwesomeIcon
            icon={faUsers}
            className="campaign-creators-input-icon"
          />
          <InputNumber
            min={min}
            max={max}
            precision={0}
            controls={false}
            bordered={false}
            value={value ?? undefined}
            onChange={onChange}
            className="campaign-creators-input-number"
          />
          <span className="campaign-creators-input-unit">creators</span>
        </div>
        <button
          type="button"
          className="campaign-creators-input-btn"
          onClick={() => onChange?.(Math.min(max, (value ?? 0) + 1))}
        >
          <PlusOutlined />
        </button>
      </div>
      <div className="campaign-creators-input-helper">
        {helperText && (
          <>
            <div>💡</div>
            <div>{helperText}</div>
          </>
        )}
      </div>
    </div>
  );
};

const CAMPAIGN_NAME_MAX_LENGTH = 80;

const getCampaignDraftFromResponse = (
  response: any,
): Record<string, any> | null => {
  const candidates = [
    response?.draft,
    response?.data?.draft,
    response?.drafts?.[0],
    response?.data?.drafts?.[0],
    response?.items?.[0],
    response?.data?.items?.[0],
    Array.isArray(response) ? response[0] : null,
  ];

  const foundCandidate = candidates.find(
    (item) => item && typeof item === "object" && !Array.isArray(item),
  );

  if (foundCandidate) return foundCandidate as Record<string, any>;
  if (response && typeof response === "object" && !Array.isArray(response)) {
    return response as Record<string, any>;
  }
  return null;
};

const normalizeCampaignDraftValues = (draft: Record<string, any>) => {
  const normalized: Record<string, any> = { ...draft };

  if (draft.launchDate) {
    const parsedLaunchDate = dayjs(draft.launchDate);
    if (parsedLaunchDate.isValid()) {
      normalized.launchDate = parsedLaunchDate;
    }
  }

  if (Array.isArray(draft.venues)) {
    normalized.venues = draft.venues
      .map((venue: any) => {
        if (typeof venue === "string") return { uuid: venue };
        if (venue?.uuid) return { uuid: venue.uuid };
        if (venue?.value) return { uuid: venue.value };
        return null;
      })
      .filter(Boolean);
  }

  if (draft.availableSlots != null) {
    const parsedSlots = Number(draft.availableSlots);
    if (!Number.isNaN(parsedSlots)) {
      normalized.availableSlots = parsedSlots;
    }
  }

  return normalized;
};

const hasInstructionPrefillFromDraft = (
  values: Record<string, unknown>,
): boolean => {
  const hasNonEmptyText = (value: unknown): boolean => {
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.some(hasNonEmptyText);
    if (value && typeof value === "object") {
      return Object.values(value as Record<string, unknown>).some(
        hasNonEmptyText,
      );
    }
    return false;
  };

  return (
    hasNonEmptyText(values.focusPoints) ||
    hasNonEmptyText(values.collabInstructions)
  );
};

const CampaignCreate: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch<any>();
  const [form] = Form.useForm();
  const { openPaywall } = usePaywall();
  const [isCampaignNameEdited, setIsCampaignNameEdited] = useState(false);
  const launchDateValue = Form.useWatch("launchDate", form);

  const maybePrefillCampaignName = useCallback(
    (locationName?: string) => {
      const currentName = form.getFieldValue("name") as string | undefined;
      const isEmpty = !currentName || currentName.trim().length === 0;
      if (!isEmpty) return;
      if (isCampaignNameEdited) return;

      const launchDateValueSelected = form.getFieldValue("launchDate");
      const campaignDateFormatted = launchDateValueSelected
        ? dayjs(launchDateValueSelected).format("MMM D, YYYY")
        : dayjs().format("MMM D, YYYY");

      const safeLocationName = locationName?.trim() || "Campaign";
      const computedCampaignName = `${safeLocationName} - ${campaignDateFormatted}`;
      form.setFieldValue("name", computedCampaignName);
    },
    [form, isCampaignNameEdited],
  );
  const [step, setStep] = useState<CampaignFormSteps>(
    CampaignFormSteps.CampaignDetails,
  );
  const [locationInput, setLocationInput] = useState("");
  const [venuesSelected, setVenuesSelected] = useState<{ uuid: string }[]>([]);
  const [showModalVenue, setShowModalVenue] = useState<boolean>(false);
  const [initialVenueSearch, setInitialVenueSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [locationAvailability, setLocationAvailability] =
    useState<LocationAvailability>();
  const [showVenueMapModal, setShowVenueMapModal] = useState<boolean>(false);
  const [focusPointsWarningKeyword, setFocusPointsWarningKeyword] = useState<
    string | null
  >(null);
  const { business, subscription } = useSelector(
    (state: BusinessState) => state.business,
  );
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const lastTrackedStepRef = useRef<CampaignFormSteps | null>(null);
  const hasAppliedDraftRef = useRef(false);
  const isFetchingDraftRef = useRef(false);
  const hasSkippedPromotionFromDraftRef = useRef(false);
  const [shouldSkipPromotionFromDraft, setShouldSkipPromotionFromDraft] =
    useState(false);
  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;

  const analyticsContext = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      businessId: business?._id ?? null,
      userId: loggedUser?.id ?? null,
      userEmail: loggedUser?.email ?? null,
      planType: subscription?.planName ?? null,
      currency:
        subscription?.stripeDetails?.nextPaymentDetails?.currency ?? null,
    }),
    [
      location.pathname,
      location.search,
      business?._id,
      loggedUser?.id,
      loggedUser?.email,
      subscription?.planName,
      subscription?.stripeDetails?.nextPaymentDetails?.currency,
    ],
  );

  const venues = useMemo(() => {
    return [
      ...(business?.venues?.map((item: any) => ({
        ...item,
        value: item.uuid,
        label: `${item.name} - ${item.address}`,
      })) || []),
    ];
  }, [business]);

  const createCampaign = async () => {
    setLoading(true);
    try {
      const { venues, ...values } = form.getFieldsValue(true);
      const params = {
        businessId: business._id,
        venues,
        ...values,
      };

      await postCollabsCampaign(params);

      message.success("Campaign created successfully");
      navigate("/campaigns");
    } catch (error: any) {
      console.error("Error submitting form:", error);

      message.error(
        error?.response?.data?.message || "Failed to create campaign",
      );
      setLoading(false);
    }
  };

  const onFinishForm = async () => {
    const { venues } = form.getFieldsValue(true);
    if (!venues || venues?.length === 0) {
      message.error("Please select at least one location");
      return;
    }
    if (freemium) {
      openPaywall({
        source: PaywallSource.LaunchCampaign,
        onSubscribe: createCampaign,
      });
      return;
    }
    createCampaign();
  };

  const goToStep = useCallback(
    (nextStep: CampaignFormSteps, replace = false) => {
      setStep(nextStep);

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("step", CAMPAIGN_FORM_STEP_TO_QUERY[nextStep]);
      setSearchParams(nextSearchParams, { replace });
    },
    [searchParams, setSearchParams],
  );

  const verifyNextStep = async (nextStep: CampaignFormSteps) => {
    try {
      await form.validateFields();
      if (
        step === CampaignFormSteps.CampaignDetails &&
        nextStep === CampaignFormSteps.Offer
      ) {
        trackCampaignDetailsContinueClicked(analyticsContext);
      }
      goToStep(nextStep);
    } catch (error) {
      console.error("Missing required fieldda:", error);
    }
  };

  const handleOfferContinue = useCallback(() => {
    if (shouldSkipPromotionFromDraft && !hasSkippedPromotionFromDraftRef.current) {
      hasSkippedPromotionFromDraftRef.current = true;
      goToStep(CampaignFormSteps.Instructions);
      return;
    }

    goToStep(CampaignFormSteps.Promotion);
  }, [goToStep, shouldSkipPromotionFromDraft]);

  const handleScheduleCampaignClick = useCallback(() => {
    trackCampaignScheduleClicked(analyticsContext);
    form.submit();
  }, [analyticsContext, form]);

  const handleFormValuesChange = useCallback(
    (changedValues: Record<string, unknown>) => {
      if ("launchDate" in changedValues) {
        trackCampaignStartDateSelected(analyticsContext);
      }
      if ("availableSlots" in changedValues) {
        trackCampaignInfluencerCountChanged(analyticsContext);
      }
    },
    [analyticsContext],
  );

  const handleFocusPointsBlur = useCallback(
    (event: FocusEvent<HTMLTextAreaElement>) => {
      setFocusPointsWarningKeyword(
        detectIncentivizedReviewKeyword(event.target.value),
      );
    },
    [],
  );

  const handleValueChange = useCallback(async () => {
    const { venues, ...values } = form.getFieldsValue(true);
    if (venues?.length > 0) {
      setLoading(true);
      try {
        const response: any = await checkTierAvailability({
          businessId: business._id,
          venues,
          ...values,
        });
        setLocationAvailability(response);
      } catch (error: any) {
        console.error("Error checking location availability:", error);
        message.error(
          error?.response?.data?.message ||
            "Failed to check location availability",
        );
      } finally {
        setLoading(false);
      }
    }
  }, [form, business]);

  const handleValueChangeDebounced = useMemo(
    () => debounce(handleValueChange, 400),
    [handleValueChange],
  );

  const onSaveVenue = useCallback(
    (venue: any) => {
      const uuid = venue?.uuid;
      if (!uuid) return;
      setVenuesSelected((prev) => {
        if (prev.some((v) => v.uuid === uuid)) return prev;
        const nextVenues = [...prev, { uuid }];
        form.setFieldValue("venues", nextVenues);
        maybePrefillCampaignName(venue?.name);
        handleValueChange();
        return nextVenues;
      });
      setShowModalVenue(false);
    },
    [form, maybePrefillCampaignName, handleValueChange],
  );

  const handleLocationSelect = (value: string) => {
    const venue = venues.find((v: any) => v.value === value);
    let newVenues;
    if (venue) {
      if (!venuesSelected.some((v) => v.uuid === venue.value)) {
        newVenues = [...venuesSelected, { uuid: venue.value }];
        setVenuesSelected(newVenues);
        form.setFieldValue("venues", newVenues);
        handleValueChange();
        maybePrefillCampaignName(
          venue?.name ?? venue?.label?.split(" - ")?.[0],
        );
      }
    } else {
      handleCustomLocation(value);
    }
    setLocationInput("");
  };

  const handleRemoveLocation = (uuid: string) => {
    const newVenues = venuesSelected.filter((l) => l.uuid !== uuid);
    setVenuesSelected(newVenues);
    form.setFieldValue("venues", newVenues);
    handleValueChange();
  };

  const handleCustomLocation = (input: string) => {
    if (!input || venuesSelected.some((v) => v.uuid === input)) return;
    setInitialVenueSearch(input);
    setShowModalVenue(true);
  };

  const handleClearSelection = () => {
    setVenuesSelected([]);
    form.setFieldValue("venues", []);
    handleValueChange();
  };

  const filteredVenues = useMemo(() => {
    const sortVenues = (a: any, b: any) => {
      const aDisabled = !!a.disabled;
      const bDisabled = !!b.disabled;
      const diff = Number(aDisabled) - Number(bDisabled);
      if (diff !== 0) return diff;
      return a.label.localeCompare(b.label);
    };

    const filtered = locationInput
      ? venues.filter((venue: any) =>
          venue.label?.toLowerCase().includes(locationInput?.toLowerCase()),
        )
      : venues;

    return filtered.sort(sortVenues);
  }, [locationInput, venues]);

  useEffect(() => {
    if (lastTrackedStepRef.current === step) return;

    lastTrackedStepRef.current = step;
    const stepMeta = getCampaignStepMeta(getCampaignStepQuery(step));
    trackCampaignStepViewed(analyticsContext, stepMeta);
  }, [analyticsContext, step]);

  useEffect(() => {
    const campaignName = form.getFieldValue("name");
    if (isCampaignNameEdited && !!campaignName) return;

    const selectedVenues = form.getFieldValue("venues") as
      | Array<{ uuid: string }>
      | undefined;
    const firstVenueUuid = selectedVenues?.[0]?.uuid;
    const firstVenue = firstVenueUuid
      ? venues.find((v: any) => v.value === firstVenueUuid)
      : undefined;
    const locationName =
      firstVenue?.name ?? firstVenue?.label?.split(" - ")?.[0];
    if (!locationName) return;

    const campaignDateFormatted = launchDateValue
      ? dayjs(launchDateValue as any).format("MMM D, YYYY")
      : dayjs().format("MMM D, YYYY");
    const computedCampaignName = `${locationName} - ${campaignDateFormatted}`;
    const currentName = form.getFieldValue("name");

    if (currentName !== computedCampaignName) {
      form.setFieldValue("name", computedCampaignName);
    }
  }, [launchDateValue, isCampaignNameEdited, form, venues]);

  useEffect(() => {
    if (!ENABLE_CAMPAIGN_STEP_QUERY_ENTRY) return;

    const nextStep = getCampaignStepFromSearchParams(searchParams);
    if (nextStep === step) return;

    setStep(nextStep);
  }, [searchParams, step]);

  useEffect(() => {
    const currentQueryStep = searchParams.get("step")?.toLowerCase();
    const expectedQueryStep = CAMPAIGN_FORM_STEP_TO_QUERY[step];

    if (currentQueryStep === expectedQueryStep) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("step", expectedQueryStep);
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams, step]);

  useEffect(() => {
    form.resetFields();
    setIsCampaignNameEdited(false);
    setShouldSkipPromotionFromDraft(false);
    hasSkippedPromotionFromDraftRef.current = false;
    dispatch(getLoggedUserLocation());
  }, [dispatch, form]);

  useEffect(() => {
    const fetchCampaignDrafts = async () => {
      const businessId = business?._id;
      if (!businessId) return;
      if (hasAppliedDraftRef.current || isFetchingDraftRef.current) return;

      isFetchingDraftRef.current = true;

      try {
        const draftsResponse: any = await getCollabsCampaignDrafts({
          businessId,
        });
        const rawDraft = getCampaignDraftFromResponse(draftsResponse);

        if (!rawDraft) return;

        const draftValues = normalizeCampaignDraftValues(rawDraft);
        form.setFieldsValue(draftValues);

        if (hasInstructionPrefillFromDraft(draftValues)) {
          setShouldSkipPromotionFromDraft(true);
        }

        const draftVenues = Array.isArray(draftValues.venues)
          ? (draftValues.venues as Array<{ uuid: string }>)
          : [];
        if (draftVenues.length > 0) {
          setVenuesSelected(draftVenues);
        }

        if (
          typeof draftValues.name === "string" &&
          draftValues.name.trim().length > 0
        ) {
          setIsCampaignNameEdited(true);
        }

        if (draftVenues.length > 0) {
          await handleValueChange();
        }

        hasAppliedDraftRef.current = true;
        console.log(
          "Campaign drafts fetched for new campaign flow:",
          draftsResponse,
        );
      } catch (error) {
        console.error("Error fetching campaign drafts:", error);
      } finally {
        isFetchingDraftRef.current = false;
      }
    };

    fetchCampaignDrafts();
  }, [business?._id, form, handleValueChange]);

  useLayoutEffect(() => {
    scrollToTop();
  }, [step]);

  if (step === CampaignFormSteps.Promotion) {
    return (
      <div style={{ padding: "24px 0" }}>
        <Promotions
          form={form}
          onContinue={() => {
            trackCampaignCollaborationContinueClicked(analyticsContext);
            goToStep(CampaignFormSteps.Instructions);
          }}
          onBack={() => goToStep(CampaignFormSteps.Offer)}
          onPromotionTypeSelected={(promotionType) => {
            if (promotionType === "creative-freedom") {
              trackCampaignCreativeFreedomSelected(analyticsContext);
            }
            if (promotionType === "discounted-offers") {
              trackCampaignPromoOffersSelected(analyticsContext);
            }
          }}
          startingStepNumber={3}
          totalSteps={5}
        />
      </div>
    );
  }

  return (
    <Row className="campaign create">
      <ModalVenue
        open={showModalVenue}
        onCancel={() => setShowModalVenue(false)}
        onSave={onSaveVenue}
        initialSearch={initialVenueSearch}
      />

      <VenueSelectionModal
        isOpen={showVenueMapModal}
        onClose={(selected) => {
          setShowVenueMapModal(false);
          if (selected && Array.isArray(selected)) {
            // Add new venues to the existing selection, avoiding duplicates
            const existingUuids = venuesSelected.map((v) => v.uuid);
            const newVenues = [
              ...venuesSelected,
              ...selected.filter((v) => !existingUuids.includes(v.uuid)),
            ];
            setVenuesSelected(newVenues);
            form.setFieldValue("venues", newVenues);
            handleValueChange();
            const firstVenueUuid = newVenues?.[0]?.uuid;
            const firstVenue = firstVenueUuid
              ? venues.find((v: any) => v.value === firstVenueUuid)
              : undefined;
            const firstVenueName =
              firstVenue?.name ??
              firstVenue?.label?.split(" - ")?.[0] ??
              firstVenueUuid;
            maybePrefillCampaignName(firstVenueName);
          }
        }}
        venues={venues.filter((v) => !v.disabled) as any}
        initialSelected={
          venuesSelected
            .map((v) => venues.find((venue: any) => venue.value === v.uuid))
            .filter(Boolean) as any
        }
      />
      <Col xs={24}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinishForm}
          onValuesChange={handleFormValuesChange}
          initialValues={{
            // name: `Campaign - ${dayjs().format("MMM D, YYYY")}`,
            // minFollowersAmount: 2000,
            // foodComp: 50,
            launchDate: dayjs().add(2, "days"),
            socialMediaName: "instagram",
            availableSlots: 1,
            // compensationTier: {
            //   food: true,
            // },
            tiers: [
              // {
              //   _id: "food",
              //   name: "Barter Comp Only",
              //   enabled: true,
              //   minFollowersAmount: 2000,
              //   maxFollowersAmount: 50000,
              //   minEngagement: 1,
              //   engagementRate: 1,
              //   foodCompAmount: 50,
              //   minCashCompAmount: 0,
              //   maxCashCompAmount: 0,
              //   guestAllowed: true,
              // },
              // {
              //   _id: "medium",
              //   name: "Medium Comp",
              //   enabled: false,
              //   minFollowersAmount: 10000,
              //   maxFollowersAmount: 100000,
              //   minEngagement: 2,
              //   engagementRate: 2,
              //   foodCompAmount: 75,
              //   minCashCompAmount: 30,
              //   maxCashCompAmount: 100,
              //   guestAllowed: true,
              // },
              // {
              //   _id: "large",
              //   name: "Large Comp",
              //   enabled: false,
              //   minFollowersAmount: 50000,
              //   maxFollowersAmount: 500000,
              //   minEngagement: 2,
              //   engagementRate: 2,
              //   foodCompAmount: 100,
              //   minCashCompAmount: 100,
              //   maxCashCompAmount: 200,
              //   guestAllowed: true,
              // },
              {
                _id: "food",
                name: "Gifted",
                enabled: true,
                minFollowersAmount: 2000,
                minEngagement: 1,
                engagementRate: 1,
                foodCompAmount: 75,
                guestAllowed: true,
              },
              {
                _id: "medium",
                name: "Paid",
                enabled: false,
                minFollowersAmount: 2000,
                minEngagement: 1,
                engagementRate: 1,
                foodCompAmount: 75,
                maxCashCompAmount: 100,
                guestAllowed: true,
              },
              // {
              //   _id: "large",
              //   name: "Large Comp",
              //   enabled: false,
              //   minFollowersAmount: 2000,
              //   minEngagement: null,
              //   engagementRate: 1,
              //   foodCompAmount: 75,
              //   minCashCompAmount: 100,
              //   maxCashCompAmount: 200,
              //   guestAllowed: true,
              // },
            ],
            minRating: 100,
          }}
        >
          {step !== CampaignFormSteps.Offer && (
            <Card style={{ borderRadius: 8 }} bordered={false}>
              <Row>
                <Col xs={16}>
                  <Typography.Title
                    level={3}
                    style={{ fontSize: 32, margin: 0 }}
                  >
                    {step === CampaignFormSteps.CampaignDetails &&
                      "Campaign Details"}

                    {step === CampaignFormSteps.Instructions && "Instructions"}
                  </Typography.Title>
                </Col>
                <Col
                  xs={8}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    color: "#666666",
                    fontSize: 16,
                  }}
                >
                  Step {step + 1} / 5
                </Col>
              </Row>{" "}
              {step === CampaignFormSteps.CampaignDetails && (
                <div className="campaign-details">
                  <Row gutter={24} style={{ marginBottom: 24 }}>
                    <Col xs={24}>
                      <Form.Item
                        label="Campaign Name"
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: "Please enter a campaign name",
                          },
                          {
                            max: CAMPAIGN_NAME_MAX_LENGTH,
                            message:
                              "Campaign name cannot exceed 80 characters",
                          },
                        ]}
                      >
                        <Input
                          placeholder="e.g., Tammy's Beautiful Cupcakes"
                          className="campaign-venues"
                          autoComplete="off"
                          maxLength={CAMPAIGN_NAME_MAX_LENGTH}
                          showCount
                          style={{ padding: 14, height: 53 }}
                          onChange={(ev) => {
                            setIsCampaignNameEdited(!!ev.target?.value?.trim());
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24} style={{ flexWrap: "nowrap" }}>
                    <Col flex={1}>
                      <Form.Item
                        label="Add Locations"
                        name="venues"
                        rules={[
                          {
                            validator: (_, value) =>
                              value && value.length > 0
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(
                                      "Please select at least one location",
                                    ),
                                  ),
                          },
                        ]}
                      >
                        <>
                          <Select
                            showSearch
                            value={locationInput}
                            placeholder="Search for brand locations..."
                            className="campaign-venues"
                            onSearch={setLocationInput}
                            onChange={handleLocationSelect}
                            notFoundContent={
                              locationInput &&
                              !filteredVenues.some(
                                (v: any) => v.label === locationInput,
                              ) ? (
                                <div style={{ padding: "8px", color: "#888" }}>
                                  Press “Enter” to add a venue
                                </div>
                              ) : null
                            }
                            onInputKeyDown={(e) => {
                              if (e.key === "Enter" && locationInput) {
                                if (
                                  !filteredVenues.some(
                                    (v: any) => v.label === locationInput,
                                  )
                                ) {
                                  handleCustomLocation(locationInput);
                                  setLocationInput("");
                                }
                              }
                            }}
                            filterOption={false}
                            options={filteredVenues}
                          />
                          <div style={{ display: "flex", minHeight: 40 }}>
                            <div className="campaign-tags" style={{ flex: 1 }}>
                              {venuesSelected.map(({ uuid }) => {
                                const venue = venues.find(
                                  (v: any) => v.value === uuid,
                                );
                                return (
                                  <div
                                    key={uuid}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      background: "#f2f0de",
                                      borderRadius: 16,
                                      padding: "6px 12px",
                                      marginBottom: 4,
                                      fontSize: 14,
                                      height: 32,
                                      fontWeight: 500,
                                      width: 243,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <span
                                      style={{
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {venue ? venue.label : uuid}
                                    </span>
                                    <button
                                      onClick={() => handleRemoveLocation(uuid)}
                                      style={{
                                        background: "none",
                                        border: "none",
                                        color: "#888",
                                        cursor: "pointer",
                                        marginLeft: 8,
                                        fontSize: 16,
                                        lineHeight: 1,
                                        padding: 0,
                                      }}
                                      aria-label="Remove"
                                      type="button"
                                    >
                                      x
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                            {venuesSelected.length > 0 && (
                              <Col
                                style={{
                                  display: "flex",
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={handleClearSelection}
                                  style={{
                                    backgroundColor: "#fff",
                                    color: "#666",
                                    textDecoration: "underline",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 14,
                                    height: 32,
                                  }}
                                >
                                  Clear Selection
                                </button>
                              </Col>
                            )}
                          </div>
                        </>
                      </Form.Item>

                      {/* <VenueMap
                          venues={venues}
                          selectedVenueUuids={selectedVenueUuids}
                          onSelectionChange={handleMapSelectionChange}
                        /> */}
                    </Col>

                    <Col
                      flex="170px"
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginTop: 50,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          trackCampaignLocationAddOnMap(analyticsContext);
                          setShowVenueMapModal(true);
                        }}
                        style={{
                          backgroundColor: "#fff7ed",
                          color: "#666",
                          border: "1px solid #fed7ab",
                          padding: "8px 16px",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontSize: 16,
                        }}
                      >
                        Add on map
                      </button>
                    </Col>
                  </Row>
                  <Row gutter={24} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="When Do You Want to Start Promoting It?"
                        name="launchDate"
                      >
                        <DatePicker
                          format="MM/DD/YYYY"
                          style={{
                            width: "100%",
                            fontWeight: 500,
                            fontSize: 18,
                            padding: 14,
                            height: 53,
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="How many creators can participate in this campaign?"
                        name="availableSlots"
                        rules={[
                          {
                            required: true,
                            message: "Please enter the number of creators",
                          },
                        ]}
                      >
                        <CreatorsNumberInput
                          min={1}
                          max={999}
                          locationCount={venuesSelected.length}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <div
                    className="campaign-availability"
                    style={{
                      background: "#fcf7ee",
                      borderRadius: 12,
                      padding: 24,
                      marginBottom: 32,
                    }}
                  >
                    <Typography.Title
                      level={5}
                      className="campaign-availability__title"
                      style={{ fontWeight: 600 }}
                    >
                      Influencer Availability
                    </Typography.Title>
                    <Spin spinning={loading}>
                      <Row gutter={24}>
                        <Col xs={24} sm={8}>
                          <Card
                            className="campaign-availability__card"
                            style={{
                              borderRadius: 12,
                              minHeight: 120,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 12,
                                marginBottom: 4,
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faUsers}
                                style={{ color: "#ff9f1c", fontSize: 28 }}
                              />
                              <span style={{ fontSize: 32, fontWeight: 700 }}>
                                {formatNumberKMBT(
                                  locationAvailability?.amountInfluencers || 0,
                                )}
                              </span>
                            </div>
                            <div style={{ fontWeight: 500, fontSize: 19 }}>
                              Total Creators
                            </div>
                          </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Card
                            className="campaign-availability__card"
                            style={{
                              borderRadius: 12,
                              minHeight: 120,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 12,
                                marginBottom: 4,
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faBullseye}
                                style={{ color: "#ff9f1c", fontSize: 28 }}
                              />
                              <span style={{ fontSize: 32, fontWeight: 700 }}>
                                {formatNumberKMBT(
                                  locationAvailability?.totalFollowersReach ||
                                    0,
                                )}
                              </span>
                            </div>
                            <div style={{ fontWeight: 500, fontSize: 19 }}>
                              Total Local Followers
                            </div>
                          </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Card
                            className="campaign-availability__card"
                            style={{
                              borderRadius: 12,
                              minHeight: 120,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 12,
                                marginBottom: 4,
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faMapMarkerAlt}
                                style={{ color: "#ff9f1c", fontSize: 28 }}
                              />
                              <span style={{ fontSize: 32, fontWeight: 700 }}>
                                {formatNumberKMBT(venuesSelected.length)}
                              </span>
                            </div>
                            <div style={{ fontWeight: 500, fontSize: 19 }}>
                              Locations Selected
                            </div>
                          </Card>
                        </Col>
                      </Row>
                    </Spin>
                  </div>
                  <Row
                    style={{
                      gap: 32,
                      marginTop: 32,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => verifyNextStep(CampaignFormSteps.Offer)}
                    >
                      Continue
                    </Button>
                  </Row>
                </div>
              )}
              {step === CampaignFormSteps.Instructions && (
                <Row>
                  <Col xs={24}>
                    <Form.Item
                      required
                      label={
                        <span
                          className="label-form"
                          style={{
                            fontSize: 20,
                            fontWeight: 600,
                            marginRight: 8,
                          }}
                        >
                          Brief
                        </span>
                      }
                      tooltip={{
                        title:
                          "Creators will receive these instructions after they have been admitted into the collab.Describe what you would like creators to focus on. For example, mention a dish you want to highlight or a specific offer they should promote. ",
                        icon: <QuestionCircleOutlined />,
                      }}
                      className="collab-focus-points"
                    >
                      <span
                        className="extra-label"
                        style={{
                          fontSize: 18,
                          marginBottom: 8,
                          display: "block",
                          color: "#666666",
                        }}
                      >
                        Specify highlights, including dishes or events.
                        Instructions are sent to creators after they are
                        approved into the collab.
                      </span>
                      <Form.Item
                        name="focusPoints"
                        noStyle
                        rules={[{ required: true }]}
                      >
                        <Input.TextArea
                          rows={5}
                          onBlur={handleFocusPointsBlur}
                          className={
                            focusPointsWarningKeyword
                              ? "incentivized-review-warning-textarea"
                              : undefined
                          }
                        />
                      </Form.Item>
                      <IncentivizedReviewWarning
                        visible={Boolean(focusPointsWarningKeyword)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Row
                      style={{
                        flexDirection: "column",
                        marginBottom: 32,
                      }}
                    >
                      <Typography.Text
                        style={{
                          paddingBottom: 8,
                          display: "block",
                          fontSize: 22,
                          fontWeight: 600,
                        }}
                      >
                        Deliverables
                      </Typography.Text>
                      <span
                        className="extra-label"
                        style={{
                          fontSize: 18,
                          marginBottom: 24,
                          display: "block",
                          color: "#666666",
                          lineHeight: 1.3,
                        }}
                      >
                        Set specific instructions for each deliverable type to
                        guide creators on exactly what content to create and how
                        to promote your brand.
                      </span>
                      <CollabSection
                        type="instagram-reels"
                        namePrefix={["collabInstructions", "instagramReel"]}
                      />
                      <CollabSection
                        type="instagram-story"
                        namePrefix={["collabInstructions", "instagramStory"]}
                      />
                      <CollabSection
                        type="tiktok-video"
                        namePrefix={["collabInstructions", "tiktokVideo"]}
                      />
                    </Row>
                    <Row
                      style={{
                        gap: 32,
                        marginTop: 32,
                        justifyContent: "space-between",
                      }}
                    >
                      <Button
                        className="text-strong-large"
                        onClick={() => goToStep(CampaignFormSteps.Promotion)}
                      >
                        Back
                      </Button>

                      <Button
                        type="primary"
                        size="large"
                        disabled={loading}
                        onClick={handleScheduleCampaignClick}
                      >
                        Schedule Campaign
                      </Button>
                    </Row>
                  </Col>
                </Row>
              )}
            </Card>
          )}

          {step === CampaignFormSteps.Offer && (
            <>
              <Row>
                <Col xs={16}>
                  <Typography.Title
                    level={3}
                    style={{ fontSize: 32, margin: 0 }}
                  >
                    {step === CampaignFormSteps.Offer && "Your Offer"}
                  </Typography.Title>
                </Col>
                <Col
                  xs={8}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    color: "#666666",
                    fontSize: 16,
                  }}
                >
                  Step {step + 1} / 5
                </Col>
              </Row>
              <OfferStep
                form={form}
                loading={loading}
                locationAvailability={locationAvailability}
                handleValueChange={handleValueChange}
                handleValueChangeDebounced={handleValueChangeDebounced}
                onClickNext={handleOfferContinue}
                onClickBack={() => goToStep(CampaignFormSteps.CampaignDetails)}
                onCompensationGiftedSelected={() =>
                  trackCampaignCompensationGiftedSelected(analyticsContext)
                }
                onCompensationPaidSelected={() =>
                  trackCampaignCompensationPaidSelected(analyticsContext)
                }
                onPlatformInstagramSelected={() =>
                  trackCampaignPlatformInstagramSelected(analyticsContext)
                }
                onPlatformTiktokSelected={() =>
                  trackCampaignPlatformTiktokSelected(analyticsContext)
                }
                onOfferContinueClicked={() =>
                  trackCampaignOfferContinueClicked(analyticsContext)
                }
              />
            </>
          )}
        </Form>
      </Col>
    </Row>
  );
};

export default CampaignCreate;
